import { useRef, useEffect } from "react";
import { ProductImageWithFallbacks } from "./ProductImageWithFallbacks";
import { BtnContent } from "./BtnIcon";
import { IoRefreshOutline } from "react-icons/io5";

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

export const GIFT_LID_POSITION_PRESETS = [
  { id: "top-left", label: "Top left", xPct: 14, yPct: 16, anchorX: 0, anchorY: 0 },
  { id: "top-right", label: "Top right", xPct: 86, yPct: 16, anchorX: -100, anchorY: 0 },
  { id: "center", label: "Center", xPct: 50, yPct: 42, anchorX: -50, anchorY: -50 },
  { id: "bottom-left", label: "Bottom left", xPct: 14, yPct: 68, anchorX: 0, anchorY: -100 },
  { id: "bottom-right", label: "Bottom right", xPct: 86, yPct: 68, anchorX: -100, anchorY: -100 },
];

const PRESET_BY_ID = Object.fromEntries(
  GIFT_LID_POSITION_PRESETS.map((preset) => [preset.id, preset]),
);

function resolvePositionId(raw) {
  if (raw?.position && PRESET_BY_ID[raw.position]) return raw.position;
  const x = Number(raw?.xPct);
  const y = Number(raw?.yPct);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return "center";
  let best = "center";
  let bestDist = Infinity;
  for (const preset of GIFT_LID_POSITION_PRESETS) {
    const dist = (x - preset.xPct) ** 2 + (y - preset.yPct) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      best = preset.id;
    }
  }
  return best;
}

export function normalizeGiftLidLayout(raw) {
  const scale = clamp(Number(raw?.scale) || 1, 0.55, 2.25);
  const position = resolvePositionId(raw);
  const preset = PRESET_BY_ID[position] || PRESET_BY_ID.center;
  return {
    position,
    xPct: preset.xPct,
    yPct: preset.yPct,
    anchorX: preset.anchorX,
    anchorY: preset.anchorY,
    scale,
  };
}

export function giftLidPositionLabel(layout) {
  const { position } = normalizeGiftLidLayout(layout);
  return PRESET_BY_ID[position]?.label || "Center";
}

export const GIFT_LID_TEXT_COLORS = ["gold", "silver"];

export function normalizeGiftLidTextColor(raw) {
  return raw === "silver" ? "silver" : "gold";
}

/**
 * Product photo as a “box lid”, with preset message positions
 * plus a textarea for typing (mirrored on the photo).
 */
export default function GiftBoxLidEditor({
  imageUrls = [],
  message,
  onMessageChange,
  maxLength,
  layout,
  onLayoutChange,
  textColor = "gold",
  onTextColorChange,
  editorId = "gift-lid-msg",
  countId = "gift-lid-count",
}) {
  const layoutRef = useRef(layout);

  useEffect(() => {
    layoutRef.current = layout;
  }, [layout]);

  const { position, xPct, yPct, anchorX, anchorY, scale } = normalizeGiftLidLayout(layout);
  const lidTextColor = normalizeGiftLidTextColor(textColor);

  const applyPosition = (positionId) => {
    const preset = PRESET_BY_ID[positionId];
    if (!preset) return;
    onLayoutChange({
      ...normalizeGiftLidLayout(layoutRef.current),
      position: preset.id,
      xPct: preset.xPct,
      yPct: preset.yPct,
      anchorX: preset.anchorX,
      anchorY: preset.anchorY,
    });
  };

  const displayText = message.trim() || "Your message";
  const hasPhoto = Array.isArray(imageUrls) && imageUrls.some(Boolean);

  return (
    <div className="gift-lid-board">
      <div
        className={`gift-lid-board__frame${hasPhoto ? "" : " gift-lid-board__frame--fallback"}`}
        role="img"
        aria-label="Gift box preview with your message"
      >
        {hasPhoto ? (
          <ProductImageWithFallbacks
            urls={imageUrls}
            alt=""
            className="gift-lid-board__photo"
            loading="lazy"
          />
        ) : null}

        <div className="gift-lid-board__veil" aria-hidden />

        <div
          className={`gift-lid-board__caption-shell gift-lid-board__caption-shell--${position}`}
          style={{
            left: `${xPct}%`,
            top: `${yPct}%`,
            transform: `translate(${anchorX}%, ${anchorY}%) scale(${scale})`,
          }}
          aria-hidden
        >
          <p className={`gift-lid-board__caption gift-lid-board__caption--${lidTextColor}`}>
            {displayText}
          </p>
        </div>
      </div>

      <div className="gift-lid-board__controls">
        <div className="gift-lid-board__field-wrap">
          <label htmlFor={editorId} className="gift-lid-board__label">
            Message (typed here, shown on the box above)
          </label>
          <textarea
            id={editorId}
            className="gift-lid-board__textarea"
            maxLength={maxLength}
            rows={4}
            placeholder="Happy birthday…"
            value={message}
            onChange={(e) => onMessageChange(e.target.value.slice(0, maxLength))}
            aria-describedby={countId}
          />
          <p id={countId} className="gift-lid-board__count">
            {message.length} / {maxLength}
          </p>
        </div>

        <div className="gift-lid-board__positions" role="group" aria-label="Message position on gift box">
          <span className="gift-lid-board__label">Position on gift box</span>
          <div className="gift-lid-board__position-grid">
            {GIFT_LID_POSITION_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`gift-lid-board__position-btn${
                  position === preset.id ? " gift-lid-board__position-btn--active" : ""
                }${preset.id === "center" ? " gift-lid-board__position-btn--center" : ""}`}
                aria-pressed={position === preset.id}
                onClick={() => applyPosition(preset.id)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        <div className="gift-lid-board__scale">
          <label htmlFor={`${editorId}-scale`} className="gift-lid-board__label">
            Text size on gift box
          </label>
          <div className="gift-lid-board__scale-row">
            <span className="gift-lid-board__scale-tick" aria-hidden>
              A
            </span>
            <input
              id={`${editorId}-scale`}
              type="range"
              min="0.55"
              max="2.25"
              step="0.05"
              value={scale}
              onChange={(e) =>
                onLayoutChange({
                  ...normalizeGiftLidLayout(layoutRef.current),
                  scale: clamp(parseFloat(e.target.value) || 1, 0.55, 2.25),
                })
              }
            />
            <span className="gift-lid-board__scale-tick gift-lid-board__scale-tick--lg" aria-hidden>
              A
            </span>
          </div>
        </div>

        {onTextColorChange ? (
          <div className="gift-lid-board__colors" role="group" aria-label="Text colour on gift box">
            <span className="gift-lid-board__label">Text colour on gift box</span>
            <div className="gift-lid-board__color-options">
              {GIFT_LID_TEXT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`gift-lid-board__color-btn gift-lid-board__color-btn--${color}${
                    lidTextColor === color ? " gift-lid-board__color-btn--active" : ""
                  }`}
                  aria-pressed={lidTextColor === color}
                  onClick={() => onTextColorChange(color)}
                >
                  {color === "gold" ? "Gold" : "Silver"}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <button
          type="button"
          className="gift-lid-board__reset btn btn-sm btn-outline-secondary"
          onClick={() => onLayoutChange(normalizeGiftLidLayout({ position: "center", scale: 1 }))}
        >
          <BtnContent icon={IoRefreshOutline}>Reset position &amp; size</BtnContent>
        </button>
      </div>
    </div>
  );
}
